import { useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import {
  deleteOrgFile,
  getFileContextUsage,
  type ContextUsageProject,
  type ProjectsApiOptions,
} from '../../api/projects';
import { type OrgAdminFile } from '../projects/types';

const useStyles = makeStyles((theme: Theme) => ({
  warningText: {
    color: theme.palette.warning.dark,
    marginTop: theme.spacing(1),
  },
  errorText: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1),
  },
  loadingBox: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
}));

type UsageState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'loaded'; projects: ContextUsageProject[] }
  | { status: 'error' };

type Props = {
  file: OrgAdminFile | null;
  orgId: string;
  apiOptions: ProjectsApiOptions;
  isPreviewMode: boolean;
  onClose: () => void;
  onDeleted: (fileId: string) => void;
};

export function DeleteOrgFileDialog({
  file,
  orgId,
  apiOptions,
  isPreviewMode,
  onClose,
  onDeleted,
}: Props) {
  const classes = useStyles();
  const { t } = useTranslation('projects');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [usage, setUsage] = useState<UsageState>({ status: 'idle' });

  useEffect(() => {
    if (!file) {
      setUsage({ status: 'idle' });
      return undefined;
    }
    if (isPreviewMode) {
      setUsage({ status: 'loaded', projects: [] });
      return undefined;
    }
    let cancelled = false;
    setUsage({ status: 'loading' });
    getFileContextUsage(apiOptions, orgId, file.id)
      .then(projects => {
        if (!cancelled) setUsage({ status: 'loaded', projects });
      })
      .catch(() => {
        if (!cancelled) setUsage({ status: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [file, orgId, apiOptions, isPreviewMode]);

  const handleClose = () => {
    if (submitting) return;
    setError(undefined);
    onClose();
  };

  const handleConfirm = async () => {
    if (!file) return;
    setSubmitting(true);
    setError(undefined);
    try {
      if (!isPreviewMode) {
        await deleteOrgFile(apiOptions, orgId, file.id);
      }
      onDeleted(file.id);
    } catch {
      setError(t('orgAdmin.deleteDialog.error'));
    } finally {
      setSubmitting(false);
    }
  };

  // Delete stays disabled until the usage check has finished (loaded or failed),
  // including the render before the effect starts it ('idle' with a file set).
  const usageChecked = usage.status === 'loaded' || usage.status === 'error';

  return (
    <Dialog
      open={!!file}
      onClose={handleClose}
      aria-labelledby="delete-org-file-dialog-title"
      data-testid="delete-org-file-dialog"
    >
      <DialogTitle id="delete-org-file-dialog-title">
        {t('orgAdmin.deleteDialog.title')}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {t('orgAdmin.deleteDialog.description')}
        </DialogContentText>

        {usage.status === 'loading' && (
          <div className={classes.loadingBox}>
            <CircularProgress size={16} />
            <Typography variant="body2">
              {t('orgAdmin.deleteDialog.checkingUsage')}
            </Typography>
          </div>
        )}

        {usage.status === 'error' && (
          <Typography variant="body2" className={classes.warningText}>
            {t('orgAdmin.deleteDialog.usageCheckFailed')}
          </Typography>
        )}

        {usage.status === 'loaded' && usage.projects.length > 0 && (
          <>
            <Typography variant="body2" className={classes.warningText}>
              {t('orgAdmin.deleteDialog.affectedProjectsWarning', {
                count: usage.projects.length,
              })}
            </Typography>
            <List dense data-testid="delete-org-file-affected-projects">
              {usage.projects.map(p => (
                <ListItem key={p.projectId} disableGutters>
                  <ListItemText primary={p.projectName} />
                </ListItem>
              ))}
            </List>
          </>
        )}

        {error && (
          <Typography variant="body2" className={classes.errorText}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          {t('actions.cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={submitting || !usageChecked}
          variant="contained"
          color="secondary"
          data-testid="delete-org-file-confirm-button"
        >
          {t('contextFiles.deleteFile')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
